import { Component, OnInit } from "@angular/core";
import { CommonModule } from "@angular/common";
import { LucideAngularModule } from "lucide-angular";
import { FormsModule } from "@angular/forms";
import { RouterService } from "../../services/router.service";
import { Court } from "../../model/court";
import { CourtReservation } from "../../model/court-reservation";
import { CourtService } from "../../services/court.service";
import { CourtReservationService } from "../../services/court-reservation.service";
import { AuthenticationService } from "../../services/authentication.service";
import { ClubService } from "../../services/club.service";
import { UserService } from "../../services/user.service";
import { User } from "../../model/user";
import { CustomModalService } from "../../services/custom-modal.service";
import { CustomNotificationService } from "../../services/custom-notification.service";
import { PlayerService } from "../../services/player.service";
import { ScoringRulesComponent } from "../scoring-rules/scoring-rules.component";

@Component({
  selector: "app-notification",
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ScoringRulesComponent,
    LucideAngularModule,
  ],
  templateUrl: "./notification.component.html",
  styleUrl: "./notification.component.css",
})
export class NotificationComponent implements OnInit {
  constructor(
    private routerService: RouterService,
    private courtSvc: CourtService,
    private courtReservationService: CourtReservationService,
    private authService: AuthenticationService,
    private clubService: ClubService,
    private userService: UserService,
    private modalService: CustomModalService,
    private notificationService: CustomNotificationService,
    private playerService: PlayerService,
  ) {}

  courts: Court[] = [];
  upcomingMatch?: CourtReservation = undefined;
  courtReservations: CourtReservation[] = [];
  upcomingReservations: CourtReservation[] = [];
  reservationIndex = 0;
  currentUser?: User;
  clubs: any[] = [];
  upcomingMatchClubName = "";
  upcomingMatchCourtName = "";
  playerDialogVisible = false;
  newPlayer: any = null;
  playerSuggestions: any[] = [];
  selectedReservation: any = null;
  searchQuery = "";
  isLoadingCourts = false;
  isLoadingClubs = false;
  isLoadingMatches = false;
  allUsers: any[] = [];

  closePlayerDialog() {
    this.playerDialogVisible = false;
    this.searchQuery = "";
    this.playerSuggestions = [];
    this.newPlayer = null;
  }

  onSearchInput(event: any) {
    const query = event.target.value;
    this.searchQuery = query;
    if (query.length > 2) {
      this.searchPlayers({ query });
    } else {
      this.playerSuggestions = [];
    }
  }

  selectPlayer(player: any) {
    this.newPlayer = player;
    this.playerSuggestions = [];
    this.searchQuery = player.label;
  }

  ngOnInit(): void {
    this.currentUser = this.authService.currentUserValue || undefined;
    this.loadItems();
    this.loadClubs();
    this.loadUsers();
  }

  loadItems() {
    this.isLoadingCourts = true;
    this.courtSvc.getItems().subscribe((items: Court[]) => {
      this.courts = items;
      this.isLoadingCourts = false;
    });
  }

  loadClubs() {
    this.isLoadingClubs = true;
    this.clubService.getClubs().subscribe({
      next: (clubs: any[]) => {
        this.clubs = clubs;
        this.isLoadingClubs = false;
        this.loadReservations();
      },
      error: () => {
        this.isLoadingClubs = false;
        this.loadReservations();
      },
    });
  }

  loadReservations() {
    if (!this.currentUser) {
      this.isLoadingMatches = false;
      return;
    }
    this.isLoadingMatches = true;
    const startDate = new Date(2000, 0, 1);
    const endDate = new Date(2100, 0, 1);
    this.courtReservationService
      .getCourtReservationsForUser(this.currentUser.id, startDate, endDate)
      .subscribe({
        next: (reservations: any[]) => {
          this.courtReservations = reservations.map((r: any) => {
            const start = new Date(r.reservation_start_time);
            const players = (r.courtReservationPlayer || []).sort(
              (a: any, b: any) => a.position - b.position,
            );
            return {
              ...r,
              date: start.toISOString().split("T")[0],
              time: start.getHours(),
              player_ids: players.map((p: any) => p.player_id),
              courtReservationPlayer: players,
            } as CourtReservation;
          });
          this.upcomingReservations = this.getUpcomingReservations();
          if (this.upcomingReservations.length) {
            this.reservationIndex = 0;
            this.upcomingMatch = this.upcomingReservations[0];
            this.populateMatchDetails(this.upcomingMatch);
          } else {
            this.upcomingMatch = undefined;
          }
          this.isLoadingMatches = false;
        },
        error: () => {
          this.isLoadingMatches = false;
        },
      });
  }

  private loadUsers() {
    this.userService.getUsers().subscribe((res) => (this.allUsers = res));
  }

  canManagePlayers(res: CourtReservation): boolean {
    return (
      !!this.currentUser &&
      res.user_id === this.currentUser.id &&
      !res.started &&
      !!res.fixed_series_id
    );
  }

  openPlayerDialog() {
    if (!this.upcomingMatch || !this.canManagePlayers(this.upcomingMatch))
      return;
    this.selectedReservation = this.upcomingMatch;
    this.newPlayer = null;
    this.playerDialogVisible = true;
  }

  getAddablePlayers() {
    if (!this.selectedReservation) return [];
    const existing = new Set(
      (this.selectedReservation.courtReservationPlayer || []).map(
        (p: any) => p.player_id,
      ),
    );
    return this.allUsers
      .filter((u) => !existing.has(u.id))
      .map((u) => ({ label: `${u.name} ${u.last_name}`, value: u.id }));
  }

  searchPlayers(event: any) {
    const query = event.query.toLowerCase();
    this.playerSuggestions = this.getAddablePlayers().filter((p: any) =>
      p.label.toLowerCase().includes(query),
    );
  }

  addPlayer() {
    if (!this.selectedReservation || !this.newPlayer) return;
    const res = { ...this.selectedReservation };
    const players = [...(res.courtReservationPlayer || [])];
    const playerId = this.newPlayer.value;
    players.push({
      court_reservation_id: res.id,
      player_id: playerId,
      position: players.length,
    });
    res.courtReservationPlayer = players;
    res.player_ids = players.map((p: any) => p.player_id);
    this.courtReservationService.updateCourtReservation(res).subscribe({
      next: () => {
        this.notificationService.success("Added", "Player added");
        this.playerDialogVisible = false;
        this.loadReservations();
        this.newPlayer = null;
      },
      error: () => {
        this.notificationService.error("Error", "Failed to add player");
      },
    });
  }

  removePlayer(playerId: number) {
    if (
      !this.selectedReservation ||
      playerId === this.selectedReservation.user_id
    )
      return;
    const res = { ...this.selectedReservation };
    let players = (res.courtReservationPlayer || []).filter(
      (p: any) => p.player_id !== playerId,
    );
    players = players.map((p: any, idx: number) => ({
      court_reservation_id: res.id,
      player_id: p.player_id,
      position: idx,
    }));
    res.courtReservationPlayer = players;
    res.player_ids = players.map((p: any) => p.player_id);
    this.courtReservationService.updateCourtReservation(res).subscribe({
      next: () => {
        this.notificationService.success("Removed", "Player removed");
        this.playerDialogVisible = false;
        this.loadReservations();
      },
      error: () => {
        this.notificationService.error("Error", "Failed to remove player");
      },
    });
  }

  getUpcomingMatch(): CourtReservation | undefined {
    let courtReservation: CourtReservation | undefined = undefined;
    let minDate = Infinity;
    const nowDate = new Date();

    for (let reservation of this.courtReservations) {
      if (reservation.finished) continue;
      const reservationDate = new Date(reservation.reservation_start_time);
      if (reservationDate < nowDate) continue;

      const players = reservation.player_ids || [];
      if (players.includes(this.currentUser!.id)) {
        const time = reservationDate.getTime();
        if (time < minDate) {
          minDate = time;
          courtReservation = reservation;
        }
      }
    }

    return courtReservation;
  }

  getUpcomingReservations(): CourtReservation[] {
    if (!this.currentUser) return [];
    const nowDate = new Date();
    return this.courtReservations
      .filter((reservation) => {
        if (reservation.finished) return false;
        const reservationDate = new Date(reservation.reservation_start_time);
        if (reservationDate < nowDate) return false;
        const players = reservation.player_ids || [];
        return players.includes(this.currentUser!.id);
      })
      .sort(
        (a, b) =>
          new Date(a.reservation_start_time).getTime() -
          new Date(b.reservation_start_time).getTime(),
      );
  }

  populateMatchDetails(match: CourtReservation) {
    const club = this.clubs.find((c) => c.id === (match as any).club_id);
    if (club) {
      this.upcomingMatchClubName = club.name;
    } else {
      this.upcomingMatchClubName = String((match as any).club_id);
    }
    this.courtSvc.getItem(match.court_id).subscribe((c: any) => {
      this.upcomingMatchCourtName = c.name;
    });
  }

  get isLoading(): boolean {
    return this.isLoadingMatches || this.isLoadingClubs || this.isLoadingCourts;
  }

  getUpcomingMatchDate(): string {
    const d = new Date(this.upcomingMatch!.reservation_start_time);
    const year = d.getFullYear();
    const month = (d.getMonth() + 1).toString().padStart(2, "0");
    const day = d.getDate().toString().padStart(2, "0");
    return `${day}.${month}.${year}`;
  }

  getUpcomingMatchCourt(): string {
    if (this.upcomingMatchCourtName) return this.upcomingMatchCourtName;
    let court = this.courts.find(
      (court) => this.upcomingMatch!.court_id == court.id,
    );
    return court ? court.name : String(this.upcomingMatch!.court_id);
  }

  playerName(i: number): string {
    const player = this.upcomingMatch?.courtReservationPlayer?.[i];
    if (!player) return "";
    if (player.player_id == this.currentUser!.id) return "Me";
    return player.player?.full_name || "";
  }

  getUpcomingMatchTime(): string {
    const d = new Date(this.upcomingMatch!.reservation_start_time);
    const h = d.getHours().toString().padStart(2, "0");
    const m = d.getMinutes().toString().padStart(2, "0");
    return `${h}:${m}`;
  }

  getClubName(): string {
    return this.upcomingMatchClubName;
  }

  getUpcomingMatchTimeString() {
    const now = new Date();
    const matchDate = new Date(this.upcomingMatch!.reservation_start_time);
    let diff = matchDate.getTime() - now.getTime();
    if (diff <= 0) return "Your match starts soon!";
    const minutes = Math.floor(diff / 60000);
    if (minutes < 60) return `Your match starts in ${minutes} minutes`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `Your match starts in ${hours} hours`;
    const days = Math.floor(hours / 24);
    if (days < 30) return `Your match starts in ${days} days`;
    const months = Math.floor(days / 30);
    return `Your match starts in ${months} months`;
  }

  startMatch() {
    if (!this.upcomingMatch) return;
    this.courtReservationService
      .updateCourtReservation({
        ...this.upcomingMatch,
        started: true,
        admin_approve_start: false,
      })
      .subscribe(() => {
        if (this.upcomingMatch) {
          this.upcomingMatch.started = true;
        }
        this.goToYourMatch();
      });
  }

  goToYourMatch() {
    if (!this.upcomingMatch) return;
    this.routerService.navigateTo(`your-match/${this.upcomingMatch.id}`);
  }

  goToMatchHistory() {
    this.routerService.navigateTo("/match-history");
  }

  goToFriendships() {
    this.routerService.navigateTo("friendships");
  }

  goToClubMembership() {
    this.routerService.navigateTo("club-membership");
  }

  goToRbRank() {
    this.routerService.navigateTo("rb-rank");
  }

  prevReservation() {
    if (this.upcomingReservations.length <= 1) return;
    this.reservationIndex =
      (this.reservationIndex - 1 + this.upcomingReservations.length) %
      this.upcomingReservations.length;
    this.upcomingMatch = this.upcomingReservations[this.reservationIndex];
    this.populateMatchDetails(this.upcomingMatch);
  }

  nextReservation() {
    if (this.upcomingReservations.length <= 1) return;
    this.reservationIndex =
      (this.reservationIndex + 1) % this.upcomingReservations.length;
    this.upcomingMatch = this.upcomingReservations[this.reservationIndex];
    this.populateMatchDetails(this.upcomingMatch);
  }

  canCancel(res: CourtReservation): boolean {
    return true;
  }

  isWithinEightHours(res: CourtReservation): boolean {
    const now = new Date();
    const start = new Date(res.reservation_start_time);
    return start.getTime() - now.getTime() < 8 * 60 * 60 * 1000;
  }

  confirmCancelReservation() {
    if (!this.upcomingMatch) return;

    const isWithinEightHours = this.isWithinEightHours(this.upcomingMatch);
    const message = isWithinEightHours
      ? "You can cancel up to 8 hours before the match without losing qp points. Canceling 8 or less hours before the match, 100 qp points will be deducted from your rank.\n\nAre you sure you want to cancel?"
      : "You can cancel up to 8 hours before the match without losing qp points. Canceling 8 or less hours before the match, 100 qp points will be deducted from your rank.\n\nAre you sure you want to cancel?";

    this.modalService
      .confirm({
        title: "Cancel Reservation",
        message: message,
        confirmText: "Yes, Cancel",
        cancelText: "Keep Reservation",
        icon: "circle-x",
      })
      .then((confirmed) => {
        if (confirmed) {
          this.cancelCurrentReservation();
        }
      });
  }

  cancelCurrentReservation() {
    const res = this.upcomingMatch;
    if (!res || !res.id || !this.canCancel(res)) return;

    const isWithinEightHours = this.isWithinEightHours(res);

    this.courtReservationService.deleteCourtReservation(res.id).subscribe({
      next: () => {
        if (isWithinEightHours && this.currentUser) {
          this.playerService
            .updatePlayerStats({
              id: this.currentUser.id,
              pp_point_change: -100,
              wins_change: 0,
              loses_change: 0,
              point_change: 0,
            })
            .subscribe({
              next: () => {
                console.log(
                  "QP points deducted successfully for late cancellation",
                );
                this.notificationService.warning(
                  "Cancelled",
                  "Reservation cancelled. 100 QP points deducted for late cancellation.",
                );
              },
              error: (error) => {
                console.error("Failed to deduct QP points:", error);
                this.notificationService.error(
                  "Error",
                  "Reservation cancelled but failed to deduct QP points",
                );
              },
            });
        } else {
          this.notificationService.success(
            "Cancelled",
            "Reservation cancelled",
          );
        }

        this.upcomingReservations = this.upcomingReservations.filter(
          (r) => r.id !== res.id,
        );
        this.courtReservations = this.courtReservations.filter(
          (r) => r.id !== res.id,
        );
        if (this.upcomingReservations.length === 0) {
          this.upcomingMatch = undefined;
        } else {
          this.reservationIndex =
            this.reservationIndex % this.upcomingReservations.length;
          this.upcomingMatch = this.upcomingReservations[this.reservationIndex];
          this.populateMatchDetails(this.upcomingMatch);
        }
      },
      error: (error) => {
        console.error("Failed to cancel reservation:", error);
        this.notificationService.error("Error", "Failed to cancel reservation");
      },
    });
  }

  getStatusClass(): string {
    const status = this.upcomingMatch?.reservation_status || "Pending";
    switch (status.toLowerCase()) {
      case "approved":
        return "approved";
      case "pending":
        return "pending";
      case "denied":
        return "denied";
      default:
        return "pending";
    }
  }
}
